import { XIcon, EditIcon, TrashIcon, ClockIcon, FileIcon } from './Icons';
import { jsPDF } from 'jspdf';

function initials(name = '') { return name.split(' ').slice(0,2).map(n=>n[0]||'').join('').toUpperCase(); }
function avatarGradient(name = '') {
  const g = ['linear-gradient(135deg,#667eea,#764ba2)','linear-gradient(135deg,#fb866d,#e8614a)','linear-gradient(135deg,#1a2744,#2d3f6b)','linear-gradient(135deg,#22c55e,#16a34a)','linear-gradient(135deg,#f59e0b,#d97706)','linear-gradient(135deg,#8b5cf6,#7c3aed)'];
  let h = 0; for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return g[Math.abs(h) % g.length];
}
function getAlert(emp) {
  if (!emp.iqamaExpiry) return null;
  const d = Math.ceil((new Date(emp.iqamaExpiry) - new Date()) / 86400000);
  if (d < 0) return { level: 'expired', days: d };
  if (d <= 30) return { level: 'critical', days: d };
  if (d <= 90) return { level: 'warning', days: d };
  return null;
}

function Field({ label, value, danger }) {
  return (
    <div>
      <div className="text-[10.5px] font-bold text-slate-400 uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-[13.5px] break-all ${danger ? 'text-red-600 font-semibold' : 'text-slate-800'}`}>{value || '—'}</div>
    </div>
  );
}

const Section = ({ label }) => (
  <div className="sm:col-span-2 text-[11px] font-bold text-[#e8614a] uppercase tracking-widest mt-4 pb-2 border-b border-slate-100">{label}</div>
);

const alertBadge = {
  expired: 'bg-red-50 text-red-600 border-red-200',
  critical: 'bg-orange-50 text-[#e8614a] border-orange-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
};

async function loadLogoPng() {
  // /logo.webp → PNG data URL (jsPDF can't embed webp directly)
  try {
    const img = new Image();
    img.src = `${window.location.origin}/logo.webp`;
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
    const c = document.createElement('canvas');
    c.width = img.naturalWidth; c.height = img.naturalHeight;
    c.getContext('2d').drawImage(img, 0, 0);
    return { data: c.toDataURL('image/png'), w: img.naturalWidth, h: img.naturalHeight };
  } catch { return null; }
}

async function downloadOfferLetter(emp) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const M = 56; // margin
  const company = emp.sponsorship || 'Delta Hubb for Operations & Maintenance Co.';
  let y = 52;

  // header: logo + company
  const logo = await loadLogoPng();
  if (logo) {
    const h = 40, w = (logo.w / logo.h) * h;
    doc.addImage(logo.data, 'PNG', M, y - 8, w, h);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(15, 27, 51);
    doc.text(company, M + w + 12, y + 8);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(100, 116, 139);
    doc.text('Jeddah, Kingdom of Saudi Arabia', M + w + 12, y + 22);
  } else {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(15, 27, 51);
    doc.text(company, M, y + 8);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(100, 116, 139);
    doc.text('Jeddah, Kingdom of Saudi Arabia', M, y + 22);
  }
  y += 44;
  doc.setDrawColor(15, 27, 51); doc.setLineWidth(1.5);
  doc.line(M, y, W - M, y);
  y += 30;

  // centered title
  doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(15, 27, 51);
  doc.text('OFFER LETTER', W / 2, y, { align: 'center' });
  y += 26;

  // date right
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5); doc.setTextColor(30, 41, 59);
  doc.text(`Date: ${new Date().toLocaleDateString('en-GB')}`, W - M, y, { align: 'right' });
  y += 28;

  doc.text(`Dear ${emp.gender === 'Female' ? 'Ms.' : 'Mr.'} ${emp.name},`, M, y);
  y += 20;

  const intro = `${company} is pleased to offer you employment. The details of this offer are outlined below. Please review the terms carefully and return a signed copy, along with the required documents, within three (3) working days.`;
  const introLines = doc.splitTextToSize(intro, W - 2 * M);
  doc.text(introLines, M, y);
  y += introLines.length * 14 + 16;

  // terms rows
  const rows = [
    ['Position:', emp.designation || '—'],
    ['Assigned Client / Deployment Site:', emp.project || '—'],
    ['Working Days:', "As per Assigned Client's schedule"],
    ['Annual Vacation:', '21 working days'],
    ['Expected Deployment Date:', emp.joiningDate || 'To be confirmed'],
    ['Contract Length:', '1 year'],
    ['Probationary Period:', '6 months'],
    ['Medical Insurance:', 'Provided as per company policy'],
    ['Total Package:', `SAR ${(emp.totalSalary || 0).toLocaleString()}`],
    ['Accommodation:', 'Included'],
    ['Transportation:', 'Included'],
    ['Tickets:', 'As per company policy'],
  ];
  rows.forEach(([k, v]) => {
    doc.setFont('helvetica', 'normal'); doc.setTextColor(71, 85, 105);
    doc.text(k, M, y);
    doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 41, 59);
    doc.text(String(v), M + 210, y);
    y += 19;
  });
  y += 14;

  const clause = `The Iqama transfer process will be initiated upon receipt of official confirmation and approval from the assigned client, ${emp.project || ''}.`;
  doc.setFont('helvetica', 'normal'); doc.setTextColor(30, 41, 59);
  const clauseLines = doc.splitTextToSize(clause, W - 2 * M);
  doc.text(clauseLines, M, y);
  y += clauseLines.length * 14 + 14;

  doc.text('Please sign below to confirm your acceptance of the offer detailed herein.', M, y);
  y += 44;

  doc.text('Name: ______________________________', M, y);
  y += 30;
  doc.text('Signature: ______________________________', M, y);

  doc.save(`Offer Letter - ${emp.name}.pdf`);
}

export default function EmployeeDetail({ emp, onClose, onEdit, onDelete, onHistory }) {
  const alert = getAlert(emp);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15,27,51,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl w-[640px] max-w-full max-h-[90vh] flex flex-col shadow-2xl">

        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 flex-shrink-0">
          <h2 className="text-base font-bold text-slate-800">Employee Profile</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 transition-all">
            <XIcon size={16} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6">
          {/* Hero */}
          <div className="flex items-center gap-4 pb-5 border-b border-slate-100">
            <div className="w-14 h-14 rounded-full flex-shrink-0 flex items-center justify-center text-lg font-bold text-white"
              style={{ background: avatarGradient(emp.name) }}>
              {initials(emp.name)}
            </div>
            <div>
              <div className="text-[17px] font-bold text-slate-800 tracking-tight">{emp.name}</div>
              <div className="text-[13px] text-slate-400 mt-0.5">{emp.designation || 'No designation'} · {emp.project}</div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                <span className={`px-3 py-1 rounded-full text-[11px] font-semibold border ${emp.status === 'Active' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-red-50 text-red-500 border-red-200'}`}>{emp.status}</span>
                {emp.iqamaStatus === 'Renewed' && <span className="px-3 py-1 rounded-full text-[11px] font-semibold border bg-emerald-50 text-emerald-600 border-emerald-200">Iqama Renewed</span>}
                {emp.iqamaStatus === 'Expired' && <span className="px-3 py-1 rounded-full text-[11px] font-semibold border bg-red-50 text-red-500 border-red-200">Iqama Expired</span>}
                {alert && <span className={`px-3 py-1 rounded-full text-[11px] font-bold border ${alertBadge[alert.level]}`}>{alert.days < 0 ? `Expired ${Math.abs(alert.days)}d ago` : `${alert.days}d left`}</span>}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Section label="Personal" />
            <Field label="Nationality" value={emp.nationality} />
            <Field label="Gender" value={emp.gender} />
            <Field label="Date of Birth" value={emp.dob} />
            <Field label="Contact" value={emp.contact} />

            <Section label="Employment" />
            <Field label="Joining Date" value={emp.joiningDate} />
            <Field label="Sponsorship" value={emp.sponsorship} />

            <Section label="Compliance" />
            <Field label="Iqama No." value={emp.iqamaNo} />
            <Field label="Iqama Expiry" value={emp.iqamaExpiry} danger={alert?.level === 'expired'} />
            <Field label="Qiwa Start" value={emp.qiwaStart} />
            <Field label="Qiwa Expiry" value={emp.qiwaEnd} />
            <Field label="Passport No." value={emp.passportNo} />
            <Field label="Passport Expiry" value={emp.passportExpiry} />
            <Field label="Health Insurance Expiry" value={emp.healthInsuranceExpiry} />

            <Section label="Compensation" />
            <Field label="Basic Salary" value={emp.basicSalary ? `SAR ${emp.basicSalary.toLocaleString()}` : null} />
            <Field label="Allowance" value={emp.allowance ? `SAR ${emp.allowance.toLocaleString()}` : null} />
            <Field label="Total Salary" value={emp.totalSalary ? `SAR ${emp.totalSalary.toLocaleString()}` : null} />
            <Field label="IBAN" value={emp.iban} />
            <Field label="Outstanding Deduction" value={emp.deductionBalance ? `SAR ${Number(emp.deductionBalance).toLocaleString()} (max 500/month)` : null} />
          </div>

          {emp.comments && (
            <div className="mt-5 p-4 bg-slate-50 border border-slate-100 rounded-xl text-[13px] text-slate-600">{emp.comments}</div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 flex-shrink-0">
          <div className="flex gap-2">
            <button onClick={onDelete}
              className="flex items-center gap-2 px-4 py-2.5 border border-red-200 rounded-full text-sm font-semibold text-red-500 hover:bg-red-50 hover:border-red-400 transition-all">
              <TrashIcon size={14} /> Remove
            </button>
            <button onClick={onHistory}
              className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-full text-sm font-semibold text-slate-600 hover:border-slate-400 transition-all">
              <ClockIcon size={14} /> History
            </button>
            <button onClick={() => downloadOfferLetter(emp)}
              className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-full text-sm font-semibold text-slate-600 hover:border-[#fb866d] hover:text-[#e8614a] transition-all">
              <FileIcon size={14} /> Offer Letter
            </button>
          </div>
          <div className="flex gap-2.5">
            <button onClick={onClose} className="px-5 py-2.5 border border-slate-200 rounded-full text-sm font-semibold text-slate-600 hover:border-slate-400 transition-all">Close</button>
            <button onClick={onEdit}
              className="flex items-center gap-2 px-6 py-2.5 rounded-full text-white text-sm font-semibold bg-[#fb866d] hover:bg-[#e8614a] transition-all">
              <EditIcon size={14} /> Edit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}